import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  BookOpen, Code, Server, Key, BarChart3, Copy, Plus, Trash2, Eye, EyeOff,
  ChevronRight, ExternalLink, Terminal, Layers, Zap, ArrowLeft, Search,
  FileCode, Puzzle, Monitor, Rocket, CheckCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { toast } from "sonner";
import { fadeInUp, staggerContainer, staggerItem, hoverLift } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  quickstart: { label: "Quick Start", icon: Rocket, color: "#10B981" },
  core: { label: "Core Docs", icon: BookOpen, color: "#06B6D4" },
  integration: { label: "Platform Integrations", icon: Puzzle, color: "#7C3AED" },
  ide: { label: "IDE & Tools", icon: Monitor, color: "#F59E0B" },
  sdk: { label: "SDKs", icon: FileCode, color: "#3B82F6" },
};

const SUB_TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "keys", label: "API Keys", icon: Key },
  { key: "docs", label: "Documentation", icon: BookOpen },
  { key: "snippets", label: "Code Snippets", icon: Code },
];

export default function DocsTab() {
  const [subTab, setSubTab] = useState("overview");
  const [guides, setGuides] = useState([]);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideContent, setGuideContent] = useState("");
  const [guideLoading, setGuideLoading] = useState(false);
  const [portalStats, setPortalStats] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [sdkSnippets, setSdkSnippets] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const fetchGuides = useCallback(async () => {
    try { const r = await axios.get(`${API}/docs/guides`); setGuides(r.data); } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/portal/stats`); setPortalStats(r.data); } catch {}
  }, []);

  const fetchKeys = useCallback(async () => {
    try { const r = await axios.get(`${API}/mcp/keys`); setApiKeys(r.data); } catch {}
  }, []);

  const fetchSnippets = useCallback(async () => {
    try { const r = await axios.get(`${API}/mcp/sdk`); setSdkSnippets(r.data); } catch {}
  }, []);

  useEffect(() => {
    fetchGuides();
    fetchStats();
    fetchKeys();
    fetchSnippets();
  }, [fetchGuides, fetchStats, fetchKeys, fetchSnippets]);

  const loadGuide = async (id) => {
    setGuideLoading(true);
    try {
      const res = await axios.get(`${API}/docs/guides/${id}`);
      setGuideContent(res.data.content);
      setSelectedGuide(id);
      // Track page view for analytics
      axios.post(`${API}/docs/analytics/view`, { guide_id: id }).catch(() => {});
    } catch {
      setGuideContent("Failed to load guide.");
    } finally {
      setGuideLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const r = await axios.post(`${API}/mcp/keys`, { name: newKeyName || "Default" });
      toast.success("API key created! Copy it now — it won't be shown again.");
      setRevealedKeys(prev => ({ ...prev, [r.data.key_id]: r.data.api_key }));
      setCreateKeyOpen(false);
      setNewKeyName("");
      fetchKeys();
      fetchStats();
    } catch { toast.error("Failed to create key"); }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await axios.delete(`${API}/mcp/keys/${keyId}`);
      toast.success("Key revoked");
      fetchKeys();
    } catch { toast.error("Failed to revoke key"); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  // Group guides by category
  const groupedGuides = guides.reduce((acc, g) => {
    const cat = g.category || "core";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(g);
    return acc;
  }, {});

  const filteredGuides = searchQuery
    ? guides.filter(g => g.title.toLowerCase().includes(searchQuery.toLowerCase()) || g.description.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  return (
    <motion.div {...fadeInUp} className="space-y-6" data-testid="docs-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--qn-foreground)]">Developer Portal</h2>
          <p className="text-sm text-[var(--qn-foreground-muted)]">API keys, documentation, SDKs & code snippets</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => window.open(`${API}/docs`, "_blank")} data-testid="open-swagger-btn">
          <ExternalLink className="w-3 h-3 mr-1" /> Swagger UI
        </Button>
      </div>

      {/* Sub-navigation */}
      <div className="flex gap-1 p-1 bg-[var(--qn-surface)] rounded-lg w-fit" data-testid="portal-sub-tabs">
        {SUB_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => { setSubTab(t.key); setSelectedGuide(null); }}
            data-testid={`portal-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              subTab === t.key
                ? "bg-[var(--qn-primary)] text-[#0A0E1A]"
                : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ===== OVERVIEW TAB ===== */}
        {subTab === "overview" && (
          <motion.div key="overview" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            <OverviewPanel stats={portalStats} onNavigate={setSubTab} />
          </motion.div>
        )}

        {/* ===== API KEYS TAB ===== */}
        {subTab === "keys" && (
          <motion.div key="keys" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            <APIKeysPanel
              keys={apiKeys}
              revealedKeys={revealedKeys}
              onCreateOpen={() => setCreateKeyOpen(true)}
              onRevoke={handleRevokeKey}
              onCopy={copyText}
            />
          </motion.div>
        )}

        {/* ===== DOCS TAB ===== */}
        {subTab === "docs" && (
          <motion.div key="docs" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            {selectedGuide ? (
              <GuideViewer
                guideId={selectedGuide}
                content={guideContent}
                loading={guideLoading}
                guides={guides}
                onBack={() => setSelectedGuide(null)}
              />
            ) : (
              <DocsBrowser
                groupedGuides={groupedGuides}
                filteredGuides={filteredGuides}
                searchQuery={searchQuery}
                onSearch={setSearchQuery}
                onSelect={loadGuide}
              />
            )}
          </motion.div>
        )}

        {/* ===== SNIPPETS TAB ===== */}
        {subTab === "snippets" && (
          <motion.div key="snippets" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            <SnippetsPanel snippets={sdkSnippets} onCopy={copyText} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Key Dialog */}
      <Dialog open={createKeyOpen} onOpenChange={setCreateKeyOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-[var(--qn-primary)]" />Generate API Key</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">Create a new API key for MCP or REST API access.</DialogDescription>
          </DialogHeader>
          <Input
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            placeholder="Key name (e.g. Production, Staging)"
            className="bg-[var(--qn-background)] border-[var(--qn-border)]"
            data-testid="new-key-name-input"
            onKeyDown={e => e.key === "Enter" && handleCreateKey()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateKeyOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateKey} className="btn-primary" data-testid="confirm-create-key-btn">
              <Key className="w-4 h-4 mr-2" />Generate Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

/* ====================================================================
   SUB-COMPONENTS
   ==================================================================== */

function OverviewPanel({ stats, onNavigate }) {
  const cards = [
    { label: "Active API Keys", value: stats?.active_api_keys ?? 0, icon: Key, color: "#06B6D4" },
    { label: "Total API Requests", value: stats?.total_api_requests ?? 0, icon: Zap, color: "#7C3AED" },
    { label: "Workflows", value: stats?.total_workflows ?? 0, icon: Layers, color: "#F59E0B" },
    { label: "Optimizations", value: stats?.total_optimizations ?? 0, icon: BarChart3, color: "#10B981" },
  ];

  return (
    <>
      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div key={i} variants={staggerItem} whileHover={hoverLift.whileHover}>
            <Card className="card-themed">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2 text-sm text-[var(--qn-foreground-muted)]">
                  <c.icon className="w-4 h-4" style={{ color: c.color }} />
                  {c.label}
                </div>
                <div className="text-3xl font-mono font-bold" style={{ color: c.color }} data-testid={`portal-stat-${c.label.toLowerCase().replace(/ /g, '-')}`}>
                  {c.value}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickAction icon={Key} title="Generate API Key" desc="Create keys for SDK & MCP access" onClick={() => onNavigate("keys")} testId="quick-action-keys" />
        <QuickAction icon={BookOpen} title="Browse Documentation" desc="16 guides across 5 categories" onClick={() => onNavigate("docs")} testId="quick-action-docs" />
        <QuickAction icon={Code} title="Code Snippets" desc="Copy-paste SDK examples" onClick={() => onNavigate("snippets")} testId="quick-action-snippets" />
      </div>

      {/* Recent API clients */}
      {stats?.recent_clients?.length > 0 && (
        <Card className="card-themed">
          <CardHeader><CardTitle className="text-base font-medium">Recent MCP Clients</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_clients.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-[var(--qn-primary)]" />
                    <span className="font-mono">{c.client_name || c.client_id}</span>
                  </div>
                  <span className="text-[var(--qn-foreground-subtle)]">{c.last_request ? new Date(c.last_request).toLocaleString() : "—"}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function QuickAction({ icon: Icon, title, desc, onClick, testId }) {
  return (
    <motion.div whileHover={hoverLift.whileHover}>
      <Card className="card-themed cursor-pointer hover:border-[var(--qn-primary)]/30 transition-all" onClick={onClick} data-testid={testId}>
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[var(--qn-primary)]/10 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-[var(--qn-primary)]" />
          </div>
          <div>
            <div className="font-medium text-sm">{title}</div>
            <div className="text-xs text-[var(--qn-foreground-muted)]">{desc}</div>
          </div>
          <ChevronRight className="w-4 h-4 ml-auto text-[var(--qn-foreground-subtle)]" />
        </CardContent>
      </Card>
    </motion.div>
  );
}

function APIKeysPanel({ keys, revealedKeys, onCreateOpen, onRevoke, onCopy }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium">API Keys</h3>
          <p className="text-sm text-[var(--qn-foreground-muted)]">Manage keys for REST API and MCP server access</p>
        </div>
        <Button onClick={onCreateOpen} className="btn-primary" size="sm" data-testid="generate-key-btn">
          <Plus className="w-4 h-4 mr-2" />Generate New Key
        </Button>
      </div>

      {keys.length === 0 ? (
        <Card className="card-themed">
          <CardContent className="p-12 text-center text-[var(--qn-foreground-muted)]">
            <Key className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="mb-4">No API keys yet</p>
            <Button onClick={onCreateOpen} className="btn-primary" data-testid="empty-generate-key-btn">
              <Plus className="w-4 h-4 mr-2" />Generate Your First Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map(k => {
            const fullKey = revealedKeys[k.key_id];
            return (
              <Card key={k.key_id} className={`card-themed ${k.revoked ? "opacity-50" : ""}`} data-testid={`api-key-${k.key_id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[var(--qn-primary)]/10 flex items-center justify-center shrink-0">
                    <Key className="w-5 h-5 text-[var(--qn-primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{k.name}</span>
                      {k.revoked && <Badge variant="destructive" className="text-xs">Revoked</Badge>}
                    </div>
                    <div className="font-mono text-xs text-[var(--qn-foreground-subtle)] truncate">
                      {fullKey || k.api_key_masked}
                    </div>
                    <div className="text-xs text-[var(--qn-foreground-subtle)] mt-1">
                      {k.request_count} requests | Created {new Date(k.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {fullKey && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onCopy(fullKey)} data-testid={`copy-key-${k.key_id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                    {!k.revoked && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => onRevoke(k.key_id)} data-testid={`revoke-key-${k.key_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Usage hint */}
      <Card className="card-themed border-[var(--qn-primary)]/20">
        <CardContent className="p-4">
          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <Terminal className="w-4 h-4 text-[var(--qn-primary)]" />Using your API key
          </div>
          <pre className="text-xs font-mono bg-[var(--qn-background)] p-3 rounded-lg overflow-x-auto text-[var(--qn-foreground-muted)]">
{`# REST API
curl -H "Authorization: Bearer qn_your_key" \\
  ${process.env.REACT_APP_BACKEND_URL}/api/optimize

# MCP Config (Claude Desktop / Cursor)
{
  "mcpServers": {
    "qnneal": {
      "url": "${process.env.REACT_APP_BACKEND_URL}/api/mcp",
      "headers": { "Authorization": "Bearer qn_your_key" }
    }
  }
}`}
          </pre>
        </CardContent>
      </Card>
    </>
  );
}

function DocsBrowser({ groupedGuides, filteredGuides, searchQuery, onSearch, onSelect }) {
  const displayGuides = filteredGuides || null;
  const categoryOrder = ["quickstart", "core", "integration", "ide", "sdk"];

  return (
    <>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-subtle)]" />
        <Input
          value={searchQuery}
          onChange={e => onSearch(e.target.value)}
          placeholder="Search documentation..."
          className="bg-[var(--qn-surface)] border-[var(--qn-border)] pl-10"
          data-testid="docs-search-input"
        />
      </div>

      {displayGuides ? (
        <div className="space-y-2">
          {displayGuides.map(g => (
            <GuideCard key={g.id} guide={g} onClick={() => onSelect(g.id)} />
          ))}
          {displayGuides.length === 0 && (
            <div className="text-center py-8 text-[var(--qn-foreground-muted)]">No guides matching "{searchQuery}"</div>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {categoryOrder.filter(c => groupedGuides[c]).map(catKey => {
            const meta = CATEGORY_META[catKey] || { label: catKey, icon: BookOpen, color: "#6B7280" };
            return (
              <div key={catKey}>
                <div className="flex items-center gap-2 mb-3">
                  <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
                  <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</h3>
                  <Badge variant="outline" className="text-xs border-[var(--qn-border)]">{groupedGuides[catKey].length}</Badge>
                </div>
                <motion.div variants={staggerContainer} initial="initial" animate="animate" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {groupedGuides[catKey].map(g => (
                    <motion.div key={g.id} variants={staggerItem}>
                      <GuideCard guide={g} onClick={() => onSelect(g.id)} />
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function GuideCard({ guide, onClick }) {
  const catMeta = CATEGORY_META[guide.category] || { color: "#6B7280" };
  return (
    <motion.div whileHover={hoverLift.whileHover}>
      <Card
        className="card-themed cursor-pointer hover:border-[var(--qn-primary)]/30 transition-all h-full"
        onClick={onClick}
        data-testid={`guide-card-${guide.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-medium text-sm mb-1">{guide.title}</div>
              <p className="text-xs text-[var(--qn-foreground-muted)] line-clamp-2">{guide.description}</p>
            </div>
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 text-[var(--qn-foreground-subtle)]" />
          </div>
          <Badge variant="outline" className="mt-2 text-xs" style={{ borderColor: catMeta.color + "40", color: catMeta.color }}>
            {guide.category}
          </Badge>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function GuideViewer({ guideId, content, loading, guides, onBack }) {
  const guide = guides.find(g => g.id === guideId);
  return (
    <>
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="guide-back-btn" className="text-[var(--qn-foreground-muted)]">
        <ArrowLeft className="w-4 h-4 mr-2" />Back to Documentation
      </Button>
      <Card className="card-themed">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[var(--qn-primary)]" />
            {guide?.title || guideId}
          </CardTitle>
          {guide && <p className="text-sm text-[var(--qn-foreground-muted)]">{guide.description}</p>}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-[var(--qn-foreground-muted)]">Loading...</div>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none
              prose-headings:text-[var(--qn-foreground)] prose-headings:font-semibold
              prose-p:text-[var(--qn-foreground-muted)] prose-p:leading-relaxed
              prose-a:text-[var(--qn-primary)] prose-a:no-underline hover:prose-a:underline
              prose-code:text-[var(--qn-primary)] prose-code:bg-[var(--qn-surface)] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-[var(--qn-background)] prose-pre:border prose-pre:border-[var(--qn-border)] prose-pre:rounded-lg
              prose-strong:text-[var(--qn-foreground)]
              prose-li:text-[var(--qn-foreground-muted)]
              prose-hr:border-[var(--qn-border)]
            " data-testid="guide-content">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function SnippetsPanel({ snippets, onCopy }) {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSnippet = async (id) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mcp/sdk/${id}`);
      setDetail(r.data);
      setSelected(id);
    } catch { toast.error("Failed to load snippet"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div>
        <h3 className="text-base font-medium">Code Snippets & SDK Examples</h3>
        <p className="text-sm text-[var(--qn-foreground-muted)]">Copy-paste ready code for quick integration</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          {snippets.map(s => (
            <motion.div key={s.id} whileHover={hoverLift.whileHover}>
              <Card
                className={`card-themed cursor-pointer transition-all ${selected === s.id ? "ring-2 ring-[var(--qn-primary)]" : ""}`}
                onClick={() => loadSnippet(s.id)}
                data-testid={`snippet-card-${s.id}`}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <Code className="w-4 h-4 text-[var(--qn-primary)] shrink-0" />
                  <div>
                    <div className="text-sm font-medium">{s.name}</div>
                    <Badge variant="outline" className="text-xs mt-1 border-[var(--qn-border)]">{s.language}</Badge>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
          {snippets.length === 0 && (
            <Card className="card-themed">
              <CardContent className="p-6 text-center text-[var(--qn-foreground-muted)]">
                <Code className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No snippets available</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2">
          {!detail ? (
            <Card className="card-themed">
              <CardContent className="p-12 text-center text-[var(--qn-foreground-muted)]">
                <Code className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Select a snippet to view code</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-themed">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base">{detail.name}</CardTitle>
                <Button variant="outline" size="sm" onClick={() => onCopy(detail.code)} data-testid="copy-snippet-btn">
                  <Copy className="w-3 h-3 mr-1" />Copy
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-[var(--qn-foreground-muted)]">Loading...</div>
                ) : (
                  <pre className="text-xs font-mono bg-[var(--qn-background)] p-4 rounded-lg overflow-x-auto text-[var(--qn-foreground-muted)] border border-[var(--qn-border)]" style={{ maxHeight: "60vh" }}>
                    {detail.code}
                  </pre>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
